import { useRouter } from "next/router";
import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCoralDrawerContext } from "@voxmedia/duet-context/context/coral-drawer-context";
import { NotificationsDrawerContext } from "@voxmedia/duet-context/context/notifications-context";

import VergeNotificationsDrawerRoot from "./VergeNotificationsDrawerRoot";

const CORAL_DRAWER_HISTORY_STATE = {
  kind: "coral-drawer",
  openedThroughHashChange: false,
} as const;

// Coral uses the comments hash and commentID query param as drawer entry
// points, so notifications need to clear them before taking over the drawer UI.
const shouldShowCoralComments = () => {
  return (
    window.location.hash === "#comments" ||
    new URLSearchParams(window.location.search).has("commentID")
  );
};

const clearCoralCommentsLocation = () => {
  const url = new URL(window.location.href);
  url.hash = "";
  url.searchParams.delete("commentID");
  window.history.replaceState(CORAL_DRAWER_HISTORY_STATE, "", url.toString());
};

// This app wrapper is The Verge's bridge from the framework-level App shell to
// the brand-specific notifications drawer, including coordination with Coral.
const VergeNotificationsDrawerAppWrapper: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const router = useRouter();
  const { closeDrawer: closeCoralDrawer, isDrawerOpen: isCoralDrawerOpen } =
    useCoralDrawerContext();

  const exitCoralMode = useCallback(() => {
    // Coral and notifications share the right-side drawer surface, so opening
    // notifications must also dismiss any active comments route or drawer.
    const hasCommentRoute = shouldShowCoralComments();

    if (hasCommentRoute) {
      clearCoralCommentsLocation();
    }

    if (isCoralDrawerOpen || hasCommentRoute) {
      closeCoralDrawer();
    }
  }, [closeCoralDrawer, isCoralDrawerOpen]);

  const openDrawer = useCallback(() => {
    exitCoralMode();
    setIsDrawerOpen(true);
  }, [exitCoralMode]);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => {
      if (prev) {
        return false;
      }
      exitCoralMode();
      return true;
    });
  }, [exitCoralMode]);

  useEffect(() => {
    if (isCoralDrawerOpen) {
      setIsDrawerOpen(false);
    }
  }, [isCoralDrawerOpen]);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [router.asPath]);

  const contextValue = useMemo(
    () => ({
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
    }),
    [isDrawerOpen, openDrawer, closeDrawer, toggleDrawer],
  );

  return (
    <NotificationsDrawerContext.Provider value={contextValue}>
      {children}
      <VergeNotificationsDrawerRoot />
    </NotificationsDrawerContext.Provider>
  );
};

export default VergeNotificationsDrawerAppWrapper;
